import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "preview", "image", "filename", "button", "status", "progress", "result", "metrics"]
  static values = {
    moduleUrl: String,
    workerPath: String,
    corePath: String,
    langPath: String
  }

  connect() {
    this.objectUrl = null
  }

  disconnect() {
    this.revokeObjectUrl()
  }

  preview() {
    const file = this.selectedFile

    if (!file || !file.type.startsWith("image/")) {
      this.clear()
      return
    }

    this.revokeObjectUrl()
    this.objectUrl = URL.createObjectURL(file)
    this.imageTarget.src = this.objectUrl
    this.imageTarget.alt = `${file.name} のプレビュー`
    this.filenameTarget.textContent = file.name
    this.previewTarget.hidden = false
    this.resultTarget.textContent = ""
    this.metricsTarget.textContent = ""
    this.setStatus("画像を選択しました。OCRを開始すると外部OCR APIへ送信せず、このブラウザ内で処理します。")
  }

  clear() {
    this.inputTarget.value = ""
    this.imageTarget.removeAttribute("src")
    this.imageTarget.alt = ""
    this.filenameTarget.textContent = ""
    this.previewTarget.hidden = true
    this.resultTarget.textContent = ""
    this.metricsTarget.textContent = ""
    this.progressTarget.textContent = ""
    this.setStatus("画像は選択されていません。")
    this.revokeObjectUrl()
  }

  async recognize() {
    const file = this.selectedFile

    if (!file) {
      this.setStatus("先に画像を選択してください。")
      return
    }

    const totalStartedAt = performance.now()
    let worker

    this.buttonTarget.disabled = true
    this.buttonTarget.textContent = "OCR測定中"
    this.resultTarget.textContent = ""
    this.metricsTarget.textContent = ""
    this.progressTarget.textContent = ""
    this.setStatus("Tesseract.jsを読み込んでいます。初回は時間がかかります。")

    try {
      const importStartedAt = performance.now()
      const { createWorker } = await import(this.moduleUrlValue)
      const importMs = performance.now() - importStartedAt

      const initStartedAt = performance.now()
      worker = await createWorker("eng", 1, {
        workerPath: this.workerPathValue,
        corePath: this.corePathValue,
        langPath: this.langPathValue,
        logger: (message) => this.updateProgress(message)
      })
      const initMs = performance.now() - initStartedAt

      const recognizeStartedAt = performance.now()
      const { data } = await worker.recognize(file)
      const recognizeMs = performance.now() - recognizeStartedAt
      const totalMs = performance.now() - totalStartedAt

      this.resultTarget.textContent = data.text.trim() || "文字を検出できませんでした。"
      this.metricsTarget.textContent = [
        `Tesseract.js読み込み: ${this.formatMs(importMs)}`,
        `OCR初期化: ${this.formatMs(initMs)}`,
        `認識処理: ${this.formatMs(recognizeMs)}`,
        `合計: ${this.formatMs(totalMs)}`,
        `実測転送量: ${this.formatBytes(this.transferSize())}`
      ].join("\n")
      this.setStatus("OCR測定が完了しました。読み取り結果は必ず目視で確認してください。")
    } catch (error) {
      this.setStatus(`OCR測定に失敗しました: ${error.message}`)
    } finally {
      if (worker) {
        await worker.terminate()
      }

      this.buttonTarget.disabled = false
      this.buttonTarget.textContent = "OCRを測定する"
    }
  }

  updateProgress(message) {
    if (!message.status) return

    const progress = typeof message.progress === "number" ? ` ${Math.round(message.progress * 100)}%` : ""
    this.progressTarget.textContent = `${message.status}${progress}`
  }

  setStatus(message) {
    this.statusTarget.textContent = message
  }

  formatMs(value) {
    return `${Math.round(value)}ms`
  }

  formatBytes(value) {
    if (value <= 0) return "取得不可"
    if (value >= 1024 * 1024) return `${(value / 1024 / 1024).toFixed(1)}MB`
    if (value >= 1024) return `${Math.round(value / 1024)}KB`

    return `${value}B`
  }

  transferSize() {
    return performance
      .getEntriesByType("resource")
      .filter((entry) => entry.name.includes("tesseract.js") || entry.name.includes("@tesseract.js-data"))
      .reduce((sum, entry) => sum + (entry.transferSize || 0), 0)
  }

  revokeObjectUrl() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl)
      this.objectUrl = null
    }
  }

  get selectedFile() {
    return this.inputTarget.files[0]
  }
}
